import { Avatar, Button, Card, Form, Image, Input, message, Modal, notification, Space, Tag, Typography } from "antd";
import PageTitle from "components/PageTitle";
import { EventDto, EventFrequency, EventTimeFrame, EventTimes, EventType, WeekdayEnum } from "dtos/events.dto";
import DashboardLayout from "layout/DashboardLayout";
import { TbClock, TbDotsVertical } from "react-icons/tb";
import dayjs from "dayjs";
import { ReactNode, useEffect, useState } from "react";
import { getNextOccurrence, getPreviousOccurrence } from "utils/recurring.dates.util";
import styles from "./Events.module.scss";
import { ApiService } from "api/api.service";
import { formatMoney } from "utils/functions";
import { useForm } from "antd/es/form/Form";
import { ParticipantDto } from "dtos/participant.dto";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

const gridStyle: React.CSSProperties = {
  width: "25%",
  boxShadow: "none",
  border: "none",
};

const EventCard = ({
  title,
  extras,
  events,
  timeframe,
}: {
  title: string;
  events?: EventDto[];
  extras?: ReactNode;
  timeframe: EventTimeFrame;
}) => {
  const [openRegisterModal, setOpenRegisterModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<EventDto | undefined>(undefined);
  const navigate = useNavigate();
  const location = useLocation();

  if (!events || !events.length)
    return (
      <Card title={title} extra={extras} className="w-full border border-gray-200 rounded-lg">
        <p className="text-center">No events found.</p>
      </Card>
    );

  const words = (() => {
    if (timeframe === EventTimeFrame.ONGOING) return { startWord: "Started", registerWord: "" };
    if (timeframe === EventTimeFrame.UPCOMING) return { startWord: "Starting", registerWord: "Register" };
    return { startWord: "Finished", registerWord: "Register for next" };
  })();

  return (
    <Card title={title} extra={extras} className="w-full border border-gray-200 rounded-lg">
      {events.map((e: EventDto) => {
        const start = dayjs(e.start_date);
        const end =
          e.time_type === EventTimes.ONE_TIME && e.end_date ? dayjs(e.end_date) : start.add(e.duration || 0, "second");
        const next = getNextOccurrence(e);

        const timeRange = `${start.format("hh:mm A")} - ${end.format("hh:mm A")}`;

        let occurrenceTime: Date;

        if (e.time_type === EventTimes.REPEATING) {
          if (timeframe === EventTimeFrame.UPCOMING) {
            occurrenceTime = next ? new Date(next) : new Date(e.start_date);
          } else if (timeframe === EventTimeFrame.ONGOING) {
            occurrenceTime = new Date(
              dayjs(e.start_date).hour(start.hour()).minute(start.minute()).second(0).toISOString(),
            );
          } else {
            const prev = getPreviousOccurrence(e);
            occurrenceTime = prev ? new Date(prev) : new Date(e.start_date);
          }
        } else {
          occurrenceTime = new Date(e.start_date);
        }

        return (
          <Card.Grid
            key={e._id}
            style={gridStyle}
            hoverable={false}
            onClick={() => {
              navigate(`/dashboard/event/${e._id}?time=${occurrenceTime.toISOString()}`, {
                state: { from: location.pathname },
              });
            }}
            className="cursor-pointer"
          >
            <div className="flex flex-col gap-2 justify-center">
              <Image
                src={
                  e.avatar_image && e.avatar_image.length
                    ? e.avatar_image
                    : `https://placehold.co/600x400/EEE/AAA?text=${encodeURIComponent(e.name)}`
                }
                preview={false}
                style={{
                  width: "100%",
                  height: 100,
                  objectFit: "cover",
                  borderRadius: 6,
                }}
              />

              <div className="flex flex-col justify-center gap-2">
                <Tag color="blue" className="w-fit rounded-full">
                  {words.startWord}, {dayjs(e.start_date).format("DD MMM YYYY")}
                </Tag>

                {e.time_type === EventTimes.REPEATING && next ? (
                  <Tag color="processing" className={`w-fit rounded-full ${styles["gemini-border"]}`}>
                    Next, {dayjs(next).format("DD MMM YYYY")}
                  </Tag>
                ) : (
                  <div style={{ height: 24 }} />
                )}
              </div>

              <Typography.Text ellipsis={{ tooltip: e.name }} style={{ maxWidth: "100%" }}>
                {e.name}
              </Typography.Text>

              <div className="flex items-center gap-2">
                <TbClock size={12} />
                {timeRange}
              </div>

              {/* {(() => {
                const now = new Date();
                const isOneTimePast = e.time_type === EventTimes.ONE_TIME && e.end_date && new Date(e.end_date) < now;

                if (timeframe === EventTimeFrame.ONGOING) return false;
                if (isOneTimePast) return false;

                return true;
              })() && (
                <Button
                  size="small"
                  onClick={(event) => {
                    event.stopPropagation();
                    setSelectedEvent(e);
                    setOpenRegisterModal(true);
                  }}
                  className="rounded-full"
                  type="primary"
                >
                  {`${words.registerWord} (${e.type === EventType.PAID && e.price ? formatMoney(e.price) : "FREE"})`}
                </Button>
              )} */}
            </div>
          </Card.Grid>
        );
      })}

      <RegisterParticipantModal
        open={openRegisterModal}
        setOpen={setOpenRegisterModal}
        event={selectedEvent}
        timeframe={timeframe}
      />
    </Card>
  );
};

const RegisterParticipantModal = ({
  open,
  setOpen,
  event,
  timeframe,
}: {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  event?: EventDto;
  timeframe: EventTimeFrame;
}) => {
  const [form] = useForm();
  const registerParticipant = ApiService.event.useRegisterParticipant();

  if (!event) return null;

  const handleSubmit = async () => {
    const start = new Date(event.start_date);
    const end = event.end_date ? new Date(event.end_date) : new Date(start.getTime() + (event.duration || 0) * 1000);
    const now = new Date();

    if (start <= now && now <= end) {
      message.error(`This event has started.`);
      form.resetFields();
      setOpen(false);
      return;
    }

    const values = await form.validateFields();
    const time = (() => {
      if (timeframe === EventTimeFrame.ONGOING) return null;

      if (timeframe === EventTimeFrame.UPCOMING) return new Date(event.start_date);

      return getNextOccurrence(event);
    })();

    if (time === null) {
      message.error("Something went wrong.");
      return;
    }

    const payload: ParticipantDto = {
      name: values.name,
      email: values.email,
      phone: values.phone,
      event_id: event._id!,
      time,
      business_details: values.business_details,
    };

    registerParticipant.mutate(payload, {
      onSuccess: (data) => {
        console.log(data);
        const { sendPaymentPrompt, paymentUrl } = data.result;
        if (!sendPaymentPrompt) {
          message.success(`Registered succesfully!`);
        } else {
          message.success(`Details acquired. Redirecting to payment screen...`);
          if (paymentUrl && paymentUrl.length) {
            setTimeout(() => {
              window.open(paymentUrl, "_blank", "noopener");
            }, 3000);
          } else {
            message.info(`If you were not redirected within 10 seconds, please contact support.`);
          }
        }

        setOpen(false);
        form.resetFields();
      },
    });
  };

  return (
    <Modal
      open={open}
      onCancel={() => {
        setOpen(false);
        form.resetFields();
      }}
      onOk={handleSubmit}
      okText="Register"
      title="Register to Event"
      okButtonProps={{ loading: registerParticipant.isPending }}
    >
      {event.type === EventType.PAID && (
        <div className="text-xs text-red-500 mb-2 text-center">
          You will be redirected to payment screen for succesful registration.
        </div>
      )}
      <Form layout="vertical" form={form}>
        <Form.Item label="Full Name" name="name" rules={[{ required: true, message: "Name is required" }]}>
          <Input placeholder="Enter your name" />
        </Form.Item>

        <Form.Item
          label="Email"
          name="email"
          rules={[
            { required: true, message: "Email is required" },
            { type: "email", message: "Enter a valid email" },
          ]}
        >
          <Input placeholder="Enter your email" />
        </Form.Item>

        <Form.Item label="Phone" name="phone">
          <Input placeholder="Enter your phone number" />
        </Form.Item>

        <Form.Item label="Business Details">
          <Form.Item name={["business_details", "type"]} className="mb-2">
            <Input placeholder="Type (e.g., Startup, Agency)" />
          </Form.Item>

          <Form.Item name={["business_details", "name"]} className="mb-2">
            <Input placeholder="Business Name" />
          </Form.Item>

          <Form.Item name={["business_details", "category"]}>
            <Input placeholder="Category" />
          </Form.Item>
        </Form.Item>
      </Form>
    </Modal>
  );
};

const Events = () => {
  const upcomingEvents = ApiService.event.useGetPaginated({ page: 1, limit: 10, timeMode: EventTimeFrame.UPCOMING });
  const pastEvents = ApiService.event.useGetPaginated({ page: 1, limit: 10, timeMode: EventTimeFrame.PAST });
  const ongoingEvents = ApiService.event.useGetPaginated({ page: 1, limit: 10, timeMode: EventTimeFrame.ONGOING });

  const [searchParams] = useSearchParams();
  const paymentSuccess = searchParams.get("paymentSuccess");

  useEffect(() => {
    if (paymentSuccess === "true") {
      notification.success({
        message: "Payment Successful",
        description: "You have been registered for the event.",
        placement: "bottomRight",
        duration: 0,
      });
    }
  }, [paymentSuccess]);

  return (
    <DashboardLayout
    //   headerRight={
    //     <div className="relative cursor-pointer">
    //       <FiBell size={20} />
    //       <span className="absolute top-0 right-0 h-2 w-2 bg-red-600 rounded-full" />
    //     </div>
    //   }
    >
      <PageTitle pageTitle={[{ title: "Events" }]} title="Events" />
      <div className="p-4">
        <div className="flex flex-col gap-8">
          <EventCard
            title="Ongoing Events"
            extras={<TbDotsVertical className="cursor-pointer" />}
            events={ongoingEvents.data?.result.data ?? []}
            timeframe={EventTimeFrame.ONGOING}
          />
          <EventCard
            title="Upcoming Events"
            extras={<TbDotsVertical className="cursor-pointer" />}
            events={upcomingEvents.data?.result.data ?? []}
            timeframe={EventTimeFrame.UPCOMING}
          />
          <EventCard
            title="Past Events"
            extras={<TbDotsVertical className="cursor-pointer" />}
            events={pastEvents.data?.result.data ?? []}
            timeframe={EventTimeFrame.PAST}
          />
        </div>
      </div>
    </DashboardLayout>
  );
};

export default Events;
